import { useContext, useEffect, useState } from "react";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { cn } from "../../lib/utils";

// Saudação no alto da tela: quem está usando, por qual empresa, e até quando
// o acesso vale.
//
// Num sistema multiempresa em que o mesmo atendente entra na conta de mais de
// um cliente, a tela não dizia em nome de quem ele estava falando — e mandar
// mensagem pela empresa errada é o tipo de engano que só se descobre depois.
// O prazo ao lado é a segunda metade: bloqueio por vencimento chegava sem
// aviso nenhum.

// "31/12/2030" a partir do que o servidor manda (ISO). Montado por partes de
// propósito: `new Date("aaaa-mm-dd")` é meia-noite UTC e, em fuso negativo,
// volta um dia — o vencimento apareceria sempre com um dia a menos.
const formatarData = (valor) => {
  if (!valor) return null;

  const [data] = String(valor).split("T");
  const [ano, mes, dia] = data.split("-").map(Number);

  if (!ano || !mes || !dia) return null;

  return new Date(ano, mes - 1, dia).toLocaleDateString("pt-BR");
};

// Quantos dias faltam, contando por dia de calendário — não por 24h, senão
// "vence hoje às 23h" aparece como 0 dias de manhã e como vencido à tarde.
const diasAte = (valor) => {
  if (!valor) return null;

  const [data] = String(valor).split("T");
  const [ano, mes, dia] = data.split("-").map(Number);

  if (!ano || !mes || !dia) return null;

  const hoje = new Date();
  const vence = new Date(ano, mes - 1, dia);

  return Math.round(
    (vence - new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())) /
      86400000,
  );
};

const SaudacaoDoUsuario = () => {
  const { user } = useContext(AuthContext);
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      setEmpresa(null);
      return;
    }

    let vivo = true;

    api
      .get("/companies/mine")
      .then(({ data }) => vivo && setEmpresa(data))
      // Sem a empresa a saudação cai para só o nome de quem entrou: falhar a
      // consulta não pode derrubar a barra inteira.
      .catch(() => vivo && setEmpresa(null));

    return () => {
      vivo = false;
    };
  }, [user?.id]);

  if (!user?.id) return null;

  // Só o primeiro nome: "Olá Maria Fernanda dos Santos" ocupa a barra toda e
  // não diz nada a mais que "Olá Maria".
  const primeiroNome = String(user.name || "").trim().split(/\s+/)[0];

  const vencimento = formatarData(empresa?.dueDate);
  const dias = diasAte(empresa?.dueDate);
  const bloqueada = empresa?.status && empresa.status !== "active";

  // Vencido, vencendo em uma semana, ou em dia — nesta ordem, porque uma
  // empresa suspensa com data futura ainda está suspensa.
  const alerta = bloqueada || (dias !== null && dias < 0);
  const atencao = !alerta && dias !== null && dias <= 7;

  return (
    <div className="hidden min-w-0 flex-1 items-center gap-2 truncate px-1 text-sm md:flex">
      <span className="truncate text-muted-foreground">
        Olá <span className="font-semibold text-foreground">{primeiroNome}</span>
        {empresa?.name && (
          <>
            , seja bem-vindo a{" "}
            <span className="font-semibold text-foreground">{empresa.name}</span>
          </>
        )}
        !
      </span>

      {vencimento && (
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
            alerta
              ? "bg-destructive/10 text-destructive"
              : atencao
                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                : "bg-muted text-muted-foreground",
          )}
          title={
            bloqueada
              ? "O acesso desta empresa está bloqueado."
              : `Vencimento do acesso: ${vencimento}`
          }
        >
          {bloqueada
            ? `Acesso bloqueado · venceu em ${vencimento}`
            : dias !== null && dias < 0
              ? `Vencido em ${vencimento}`
              : `Ativo até ${vencimento}`}
        </span>
      )}
    </div>
  );
};

export default SaudacaoDoUsuario;
